import { Paperclip, Trash2, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { FilesField, type FileUploadState } from '../../../../components/inputs/FilesField';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { Tooltip } from '../../../../components/Tooltip';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { applicantsApi } from '../applicantsApi';
import {
  APPLICANT_ASSESSMENT_RESULT_OPTIONS,
  type ApplicantAssessment,
  type ApplicantAssessmentResultValue,
} from '../applicantTypes';
import { sampleAssessment } from '../utils/sampleRecruitment';

/** What the stage's files accept — the server's `applicant-assessment-files` category, verified by magic bytes. */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';
const MAX_BYTES = 10 * 1024 * 1024;

/** `''` is unanswered — a required result opens EMPTY, never pre-read as either. */
type ResultChoice = '' | '0' | '1';

/**
 * Records or corrects one ASSESSMENT stage (product call, 20 Oct 2026): **Result** (Passed / Failed),
 * **Remarks** (a rich-text document) and **Attachments**. No stage gates another.
 *
 * **The write order**: the result and remarks first (which creates the stage), then each file taken
 * off, then each new file — a file needs a saved stage to land on. A file that fails after the stage
 * saved is NAMED, never silently dropped, and the stage is not undone.
 */
export function ApplicantAssessmentModal({
  applicantId,
  applicantName,
  assessment,
  onClose,
  onSaved,
}: {
  applicantId: string;
  applicantName: string;
  assessment: ApplicantAssessment;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const idPrefix = `applicant-assessment-${assessment.stage}`;

  const [result, setResult] = useState<ResultChoice>(assessment.result != null ? (String(assessment.result) as ResultChoice) : '');
  const [remarks, setRemarks] = useState(assessment.remarks ?? '');
  /** Bumped by Autogenerate: the editor is uncontrolled after mount, so a generated document REMOUNTS it. */
  const [editorKey, setEditorKey] = useState(0);
  /** Filed files the reader marked to take off — applied only when the form saves. */
  const [removing, setRemoving] = useState<string[]>([]);
  const [picked, setPicked] = useState<File[]>([]);
  const [uploads, setUploads] = useState<Record<number, FileUploadState>>({});
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const oversize = picked.some((file) => file.size > MAX_BYTES);
  const valid = result !== '' && !oversize;
  const canSave = valid && confirmed && !saving;
  const isCorrection = assessment.result != null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await applicantsApi.recordAssessment(applicantId, assessment.stage, {
        result: Number(result) as ApplicantAssessmentResultValue,
        remarks: remarks.trim() === '' ? null : remarks,
      });
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the assessment.', 0);
      setError(failure);
      // A <RadioGroup> puts its id on each OPTION.
      focusValidationError(failure, { idPrefix, fieldIds: { result: `${idPrefix}-result-0` } });
      setSaving(false);
      return;
    }

    // The stage is saved from here on; a file that fails is named, and the stage stays.
    const failed: string[] = [];

    for (const attachmentId of removing) {
      try {
        await applicantsApi.removeAssessmentFile(applicantId, assessment.stage, attachmentId);
      } catch (caught) {
        const name = assessment.attachments.find((a) => a.id === attachmentId)?.fileName ?? 'a file';
        failed.push(`${name} was not removed (${caught instanceof ApiError ? caught.message : 'failed'})`);
      }
    }

    for (const [index, file] of picked.entries()) {
      try {
        await applicantsApi.attachAssessmentFile(applicantId, assessment.stage, file, (percent) =>
          setUploads((current) => ({ ...current, [index]: { percent } })),
        );
        setUploads((current) => ({ ...current, [index]: { done: true } }));
      } catch (caught) {
        const reason = caught instanceof ApiError ? caught.message : 'upload failed';
        setUploads((current) => ({ ...current, [index]: { error: reason } }));
        failed.push(`${file.name} was not attached (${reason})`);
      }
    }

    setSaving(false);

    if (failed.length > 0) {
      // The form has nothing left to retry in — the stage saved — so the failure is a toast.
      toast.failure(`${assessment.stageLabel} saved, but: ${failed.join('; ')}.`);
    } else {
      toast.success(`${assessment.stageLabel} recorded.`);
    }

    onSaved();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${isCorrection ? 'Correct' : 'Record'} ${assessment.stageLabel.toLowerCase()}`}
      size="2xl"
      id={`${idPrefix}-modal`}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleAssessment(assessment.stageLabel);
          setResult(String(sample.result) as ResultChoice);
          setRemarks(sample.remarks);
          setEditorKey((k) => k + 1);
        },
        disabled: saving,
      }}
      confirm={{ id: `${idPrefix}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${idPrefix}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${idPrefix}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <p className="text-xs text-slate-500">
          {assessment.stageLabel} for <span className="text-slate-700">{applicantName}</span>. No stage gates another — a
          Failed result here blocks nothing else.
        </p>

        <RadioGroup<ResultChoice>
          id={`${idPrefix}-result`}
          label="Result"
          required
          description="Passed earns the stage its weight in the Summary; Failed earns nothing."
          value={result}
          onChange={setResult}
          options={APPLICANT_ASSESSMENT_RESULT_OPTIONS.map((o) => ({ value: String(o.value) as ResultChoice, label: o.label }))}
          columns={2}
          error={result === '' ? 'Choose Passed or Failed.' : error?.fieldError('result')}
          disabled={saving}
        />

        <LazyRichTextField
          key={editorKey}
          id={`${idPrefix}-remarks`}
          label="Remarks"
          description="Notes on this stage. Formatting, lists and tables are kept; anything the toolbar cannot produce is removed when it is saved."
          value={remarks}
          onChange={setRemarks}
          error={error?.fieldError('remarks')}
          disabled={saving}
          placeholder="What the assessor observed…"
        />

        {assessment.attachments.length > 0 ? (
          <FiledList
            idPrefix={idPrefix}
            files={assessment.attachments}
            removing={removing}
            disabled={saving}
            onToggle={(attachmentId) =>
              setRemoving((current) =>
                current.includes(attachmentId) ? current.filter((x) => x !== attachmentId) : [...current, attachmentId],
              )
            }
          />
        ) : null}

        <FilesField
          id={`${idPrefix}-files`}
          label="Attachments"
          description="The documents for this stage. Filed once the result is saved, one at a time."
          hint="PDF, PNG or JPEG"
          accept={ACCEPT}
          maxBytes={MAX_BYTES}
          files={picked}
          onChange={(next) => {
            setPicked(next);
            setUploads({});
          }}
          uploads={uploads}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}

/**
 * The files already filed — each with a REMOVE that only marks it; nothing is taken off until the
 * form saves, and a marked row offers the undo in the same place.
 */
function FiledList({
  idPrefix,
  files,
  removing,
  disabled,
  onToggle,
}: {
  idPrefix: string;
  files: ApplicantAssessment['attachments'];
  removing: string[];
  disabled: boolean;
  onToggle: (attachmentId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm text-slate-700">Filed</span>
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
        {files.map((file) => {
          const marked = removing.includes(file.id);

          return (
            <li key={file.id} className="flex min-w-0 items-center gap-2 px-3 py-2">
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden="true" />
              <span className={`min-w-0 flex-1 truncate text-sm ${marked ? 'text-slate-400 line-through' : 'text-slate-600'}`}>
                {file.fileName}
              </span>
              {marked ? <span className="shrink-0 text-xs text-red-700">Removed on save</span> : null}
              <Tooltip label={marked ? `Keep ${file.fileName}` : `Remove ${file.fileName}`}>
                <Button
                  id={`${idPrefix}-filed-${file.id}-${marked ? 'keep' : 'remove'}`}
                  variant="tertiary"
                  aria-label={marked ? `Keep ${file.fileName}` : `Remove ${file.fileName}`}
                  onClick={() => onToggle(file.id)}
                  disabled={disabled}
                >
                  {marked ? <Undo2 className="h-4 w-4" aria-hidden="true" /> : <Trash2 className="h-4 w-4" aria-hidden="true" />}
                </Button>
              </Tooltip>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
